import { Suspense, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { ContactShadows, OrbitControls } from '@react-three/drei';
import type { Group } from 'three';
import type { RefObject } from 'react';
import { CameraRig, type ShowroomOrbitControls } from './CameraRig';
import { CarDriver } from './CarDriver';
import { CarModel } from './CarModel';
import { DrivingShadow } from './DrivingShadow';
import { VehicleHeadlights } from './VehicleHeadlights';
import { createExteriorPose } from './cameraPoses';
import { DRIVING_FLOOR_RADIUS } from '../model/stageGround';
import {
  DEFAULT_VEHICLE_SURFACE,
  STAGE_MODES,
  type StageMode,
  type VehicleInput,
  type VehicleState,
  type VehicleSurfaceCondition,
} from '../model/vehicleDriving';
import type { CarModel as CarModelSpec, SelectedOptions, StudioOption } from '../types';
import type { DrivingCourseOption } from '../data/courseOptions';
import type { TimeOfDayOption } from '../data/timeOfDayOptions';
import type { DrivingCameraOption } from '../data/drivingCameraOptions';
import type { DrivingChallengeOption, DrivingChallengeId } from '../data/challengeOptions';
import type { GraphicsQualityOption } from '../data/graphicsQualityOptions';
import type {
  DrivingAssists,
  RegenMode,
} from '../model/drivingPreferences';
import type { VehicleTuning } from '../model/vehicleTuning';
import type { DrivingSessionState } from '../model/drivingSession';
import type { DrivingChallengeState } from '../model/drivingChallenges';
import type { DrivingTelemetryState } from '../model/drivingTelemetry';
import { FrameStats, type FrameStatsValue } from './FrameStats';
import { DrivingScene } from './DrivingScene';

const WEBGL_UNAVAILABLE_FALLBACK = (
  <div className="viewer-fallback" role="alert">
    <p>이 브라우저는 WebGL을 지원하지 않아 3D 뷰어를 표시할 수 없습니다.</p>
  </div>
);

// 접지 그림자가 덮을 정사각 영역을 전장에 비례시킵니다.
// 고정값을 쓰면 차가 큰 모델에서 그림자가 잘려 바퀴 옆이 떠 보입니다.
const SHADOW_SCALE_PER_LENGTH = 2.45;

/**
 * 카메라 far plane (m). 주행 노면 지름에서 나옵니다.
 *
 * 고정값 120이었습니다. 주행 노면을 넓히면서 이 값이 노면 지름보다 작아졌고, 그러면
 * 코스 반대편이 잘려서 아스팔트가 허공에서 끝나는 것처럼 보입니다. 노면 끝에 선
 * 카메라가 반대편 끝을 보는 경우가 최악이라 지름 전체가 필요하고, 배경 하늘이 채워
 * 주지 않는 각도를 위해 여유를 조금 둡니다.
 *
 * 깊이 정밀도는 걱정하지 않아도 됩니다. 원근 깊이 버퍼의 해상도는 사실상 near에서
 * 정해지고(z²/near/2ⁿ) far에는 거의 무관해서, 120에서 216으로 늘려도 30m 지점의
 * 정밀도가 1.07mm로 같습니다.
 */
const CAMERA_FAR = DRIVING_FLOOR_RADIUS * 2 + 16;

export interface ShowroomCanvasProps {
  model: CarModelSpec;
  studio: StudioOption;
  selectedOptions: SelectedOptions;
  viewMode: string;
  stageMode: StageMode;
  /** 주행 조작 입력. DOM 쪽에서 갱신하고 주행 루프가 매 프레임 읽습니다. */
  drivingInputRef: RefObject<VehicleInput>;
  /** 주행 운동 상태. 주행 루프가 쓰고 HUD가 읽습니다. */
  drivingStateRef: RefObject<VehicleState>;
  drivingSessionRef: RefObject<DrivingSessionState>;
  drivingChallengeRef: RefObject<DrivingChallengeState>;
  drivingTelemetryRef: RefObject<DrivingTelemetryState>;
  drivingCourse: DrivingCourseOption;
  drivingAssists: DrivingAssists;
  drivingRegenMode: RegenMode;
  drivingTuning: VehicleTuning;
  drivingCamera: DrivingCameraOption;
  drivingChallenge: DrivingChallengeOption;
  timeOfDay: TimeOfDayOption;
  /** 동적 날씨가 켜지면 선택 프리셋 사이를 실시간으로 보간합니다. */
  dynamicWeather: boolean;
  graphicsQuality: GraphicsQualityOption;
  frameStatsRef: RefObject<FrameStatsValue>;
  onLapCompleted?: (courseId: string, resultSeconds: number) => void;
  onChallengeCompleted?: (challengeId: DrivingChallengeId, result: number) => void;
}

export function ShowroomCanvas({
  model,
  studio,
  selectedOptions,
  viewMode,
  stageMode,
  drivingInputRef,
  drivingStateRef,
  drivingSessionRef,
  drivingChallengeRef,
  drivingTelemetryRef,
  drivingCourse,
  drivingAssists,
  drivingRegenMode,
  drivingTuning,
  drivingCamera,
  drivingChallenge,
  timeOfDay,
  dynamicWeather,
  graphicsQuality,
  frameStatsRef,
  onLapCompleted,
  onChallengeCompleted,
}: ShowroomCanvasProps) {
  const controlsRef = useRef<ShowroomOrbitControls | null>(null);
  /*
   * 주행 루프가 매 프레임 직접 만지는 두 그룹입니다. R3F props로 넘기면 프레임마다
   * 렌더가 필요하므로 ref로 잡아 두고 객체를 직접 씁니다(CarDriver 주석 참고).
   */
  const carGroupRef = useRef<Group | null>(null);
  const bodyTiltRef = useRef<Group | null>(null);
  /*
   * 노면 조건은 식별자를 고정한 채 필드만 갱신합니다(DrivingScene 주석 참고).
   * 동적 날씨가 매 샘플마다 새 객체를 만들면 CarDriver가 그때마다 다시 렌더됩니다.
   */
  const drivingSurfaceRef = useRef<VehicleSurfaceCondition>({ ...DEFAULT_VEHICLE_SURFACE });

  const isDriving = stageMode === STAGE_MODES.DRIVING;

  /**
   * Canvas의 camera와 OrbitControls의 target은 마운트 시점 값만 의미가 있습니다.
   * 렌더마다 새 pose 객체를 넘기면 drei가 매번 target을 되돌려서 CameraRig의
   * 전환 애니메이션과 싸웁니다. 그래서 최초 pose를 ref에 고정해 두고,
   * 이후 모델·보기 모드 변경은 CameraRig가 단독으로 책임집니다.
   */
  const initialPose = useRef(createExteriorPose(model.bounds)).current;

  const modelLength = model.bounds.frontX - model.bounds.rearX;

  return (
    <Canvas
      fallback={WEBGL_UNAVAILABLE_FALLBACK}
      dpr={graphicsQuality.dpr}
      gl={{ antialias: true }}
      camera={{ position: initialPose.position, fov: initialPose.fov, near: 0.05, far: CAMERA_FAR }}
    >
      <FrameStats statsRef={frameStatsRef} />
      <DrivingScene
        studio={studio}
        selectedWeather={selectedOptions.weather}
        dynamicWeather={dynamicWeather}
        timeOfDay={timeOfDay}
        isDriving={isDriving}
        drivingCourse={drivingCourse}
        drivingStateRef={drivingStateRef}
        graphicsQuality={graphicsQuality}
        modelBounds={model.bounds}
        surfaceRef={drivingSurfaceRef}
      />

      {/* 차량 GLB는 비동기로 로드됩니다. 로딩 상태는 ViewerLoadingOverlay가 DOM에서 표시합니다. */}
      <Suspense fallback={null}>
        {/*
          주행이 차를 옮기는 지점입니다. 두 겹으로 나눈 이유:
            바깥(carGroupRef) = 월드 위치 + 진행 방향 + 지형 경사
            안쪽(bodyTiltRef) = 가감속·코너링에 따른 동적 롤 + 피치
          지형 자세와 차체 움직임을 한 그룹에 넣으면 경사 피치와 가속 피치가 서로 다른
          축에 적용되어 방향을 바꿀 때 차가 비틀립니다.

          차량을 그룹으로 묶는 또 다른 이유: CarModel은 GLB 하나가 아니라 인포테인먼트
          판과 배지 데칼을 형제로 함께 렌더하고, 그 좌표는 앱 좌표계 절대값입니다(각
          컴포넌트 주석 참고). GLB만 옮기면 판과 배지가 원래 자리에 남습니다.
        */}
        <group ref={carGroupRef}>
          <group ref={bodyTiltRef}>
            <CarModel
              model={model}
              paint={selectedOptions.paint}
              wheel={selectedOptions.wheel}
              interior={selectedOptions.interior}
              /*
                바퀴는 CarModel이 직접 돌립니다(피벗을 만든 쪽이 회전까지 책임집니다).
                주행 중이 아니면 상태가 0이라 바퀴도 정지 상태로 남습니다.
              */
              drivingStateRef={drivingStateRef}
            />
            {isDriving && timeOfDay.isDark ? <VehicleHeadlights bounds={model.bounds} /> : null}
          </group>

          {/*
            그림자는 동적 기울기 그룹 밖입니다. 차체가 가감속·코너링으로 롤·피치해도
            함께 흔들리지 않지만, 바깥 그룹의 지형 자세는 따라가므로 경사면에는 붙습니다.

            주행과 정지에서 다른 방식을 쓰는 이유:
              정지 — ContactShadows가 실루엣을 정확히 굽습니다. frames={1}로 한 번만
                굽고, 차를 바꾸면 실루엣이 달라지므로 key로 리마운트해 다시 굽습니다.
                (모델 교체 시 Suspense가 이 트리를 어차피 한 번 버리지만, 캐시된
                모델로 되돌아올 때는 버리지 않으므로 key가 필요합니다)
              주행 — 한 번 구운 그림자는 움직이는 차를 따라갈 수 없고, 매 프레임 다시
                구우면 삼각형 35만 개를 프레임마다 한 번 더 렌더합니다. 그래서 값싼
                타원 그라디언트 판으로 바꿉니다. 추격 시점에서는 실루엣의 정확도보다
                차가 지면에 붙어 보이고 경사면을 따라가는지가 더 중요합니다.
          */}
          {isDriving ? (
            <DrivingShadow bounds={model.bounds} opacity={studio.contactShadowOpacity} />
          ) : (
            <ContactShadows
              key={`${model.id}:${graphicsQuality.id}`}
              position={[0, 0.001, 0]}
              opacity={studio.contactShadowOpacity}
              scale={modelLength * SHADOW_SCALE_PER_LENGTH}
              blur={2.2}
              far={2}
              resolution={graphicsQuality.shadowResolution}
              frames={1}
            />
          )}
        </group>
      </Suspense>

      {/* 주행 모드에서만 마운트됩니다. 나갈 때 차를 무대 중심으로 되돌립니다. */}
      {isDriving ? (
        <CarDriver
          vehicleId={model.id}
          bounds={model.bounds}
          powertrain={model.powertrain}
          topSpeedKmh={model.topSpeedKmh}
          zeroTo100KmhSeconds={model.zeroTo100KmhSeconds}
          kerbWeightKg={model.kerbWeightKg}
          electricEnergy={model.electricEnergy}
          regenMode={drivingRegenMode}
          surface={drivingSurfaceRef.current}
          course={drivingCourse}
          assists={drivingAssists}
          tuning={drivingTuning}
          camera={drivingCamera}
          challenge={drivingChallenge}
          interiorEye={model.interiorEye ?? undefined}
          sessionRef={drivingSessionRef}
          challengeRef={drivingChallengeRef}
          telemetryRef={drivingTelemetryRef}
          inputRef={drivingInputRef}
          stateRef={drivingStateRef}
          carGroupRef={carGroupRef}
          bodyTiltRef={bodyTiltRef}
          onLapCompleted={onLapCompleted}
          onChallengeCompleted={onChallengeCompleted}
        />
      ) : null}

      <OrbitControls
        ref={controlsRef}
        makeDefault
        enablePan={false}
        enableDamping
        dampingFactor={0.08}
        target={initialPose.target}
      />
      <CameraRig viewMode={viewMode} model={model} stageMode={stageMode} controlsRef={controlsRef} />
    </Canvas>
  );
}
